// Error classes of the HTTP transport, shared by billing and the shell:
// api/client.ts re-exports them, so each class has one identity.

/**
 * Thrown when a mutation is queued for offline sync instead of executing immediately.
 * Callers should catch this to show an appropriate offline message without navigating.
 */
export class OfflineQueuedError extends Error {
  constructor() {
    super('Opération mise en file d\'attente pour synchronisation');
    this.name = 'OfflineQueuedError';
  }
}

/**
 * Thrown when `fetch()` fails for a write sent with `failWhenOffline`: the
 * write was not queued for offline sync. A connection reset can follow a
 * request the server received, so the message never says it was not sent.
 * A `TypeError`, as the network failure it replaces.
 */
export class OfflineNotQueuedError extends TypeError {
  public readonly code = 'HORS_LIGNE_NON_MIS_EN_FILE';

  constructor() {
    super(
      "La connexion au serveur a échoué ; l'opération n'a pas été mise en file d'attente. " +
        'Vérifiez si elle a été enregistrée avant de la recommencer.'
    );
    this.name = 'OfflineNotQueuedError';
  }
}

/**
 * API Error class for handling API errors
 */
export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  /**
   * Machine-readable payload attached by the API to a specific error code.
   * Deliberately `unknown`-valued: it used to be typed `Record<string,
   * string[]>` (validation-style), which was a LIE for any other shape —
   * TENANT_AMBIGU carries `{ tenants: [{ slug, name }] }`, and the old type
   * silently made that unreadable at the call site while the data was
   * sitting right there at runtime. Narrow it where you consume it.
   */
  public readonly details?: Record<string, unknown> | undefined;

  constructor(
    message: string,
    code: string,
    status: number,
    details?: Record<string, unknown> | undefined
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /**
   * Check if the error is a validation error
   */
  isValidationError(): boolean {
    return this.code === 'VALIDATION_ERROR' || this.status === 422;
  }

  /**
   * Check if the error is an authentication error
   */
  isAuthError(): boolean {
    return this.status === 401;
  }

  /**
   * Check if the error is a forbidden error
   */
  isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * Check if the error is a not found error
   */
  isNotFound(): boolean {
    return this.status === 404;
  }
}
